import React, { useEffect, useState } from 'react';
import { Edit3, Check, Loader2, X, Filter } from 'lucide-react';
import SegmentsCondition from 'components/segments/SegmentsCondition';
import { useDispatch } from 'react-redux';
import { getAttributesRequest } from 'store/attributes';
import { ISegmentForm } from 'types/segments/segments.types';
import {
  clearSegmentState,
  createSegmentRequest,
  getSegmentRequest,
  getSegmentsRequest,
  updateSegmentRequest,
} from 'store/segments/segments.action';
import { useToast } from 'contexts/ToastContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectSegmentsError,
  selectSegmentsLoading,
  selectSegmentsSuccess,
  selectSelectedSegment,
} from 'store/segments/segments.selector';
import { trimSegmentConditionValues } from 'utils/helper';
import { updateSegment } from 'services/segmentService/segmentService';
const SmartSegmentsForm = ({
  initialTitle = 'Unnamed Segment',
}: {
  initialTitle?: string;
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [tempTitle, setTempTitle] = useState(initialTitle);
  const selectedSegment = useSelector(selectSelectedSegment);
  const segmentLoading = useSelector(selectSegmentsLoading);
  const [segment, setSegment] = useState<ISegmentForm>({
    name: 'Unnamed Segment',
    description: '',
    conditions: {
      and: [
        {
          condition: 'attribute',
          field: '',
          operator: '',
          value: null,
        },
      ],
    },
    type: 'Smart',
  });
  const success = useSelector(selectSegmentsSuccess);
  const error = useSelector(selectSegmentsError);

  useEffect(() => {
    if (id) {
      dispatch(getSegmentRequest(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && selectedSegment) {
      setSegment({
        ...selectedSegment,
        conditions: { ...selectedSegment.conditions },
      } as ISegmentForm);
    }
  }, [id, selectedSegment]);

  useEffect(() => {
    if (success) {
      showToast({
        type: 'success',
        title: 'Segment saved',
        message: 'Segment has been saved successfully',
      });
      navigate('/segments');
      dispatch(clearSegmentState());
    }
  }, [success, navigate, dispatch, showToast]);

  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: error,
      });
      dispatch(clearSegmentState());
    }
  }, [error, showToast, dispatch]);

  useEffect(() => {
    dispatch(getAttributesRequest());
    dispatch(getSegmentsRequest({}));
  }, [dispatch]);

  const handleEdit = () => {
    setTempTitle(segment.name);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const nextName = tempTitle.trim();
    if (!nextName) {
      setTempTitle(segment.name);
      setIsEditing(false);
      return;
    }

    if (!id || nextName === segment.name) {
      setSegment({
        ...segment,
        name: nextName,
      });
      setIsEditing(false);
      return;
    }

    setNameSaving(true);
    try {
      const updated = await updateSegment({
        id,
        data: {
          name: nextName,
          description: selectedSegment?.description ?? segment.description,
          conditions: trimSegmentConditionValues(
            selectedSegment?.conditions ?? segment.conditions
          ),
          type: selectedSegment?.type ?? segment.type,
        },
      });
      setSegment((prev) => ({
        ...prev,
        name: updated?.name || nextName,
      }));
      showToast({
        type: 'success',
        title: 'Segment name updated',
        message: 'Segment name has been updated successfully',
      });
      setIsEditing(false);
    } catch (err: unknown) {
      const message =
        (
          err as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (err as { message?: string })?.message ||
        'Failed to update segment name';
      showToast({
        type: 'error',
        title: 'Error',
        message,
      });
    } finally {
      setNameSaving(false);
    }
  };

  const handleCancel = () => {
    if (nameSaving) return;
    setTempTitle(segment.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (nameSaving) return;
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleSubmit = () => {
    // Trim all string values in the segment conditions before submission
    const trimmedSegment = {
      ...segment,
      conditions: trimSegmentConditionValues(segment.conditions),
    };
    if (id) {
      dispatch(
        updateSegmentRequest({
          id: id || '',
          data: trimmedSegment,
        })
      );
    } else {
      dispatch(createSegmentRequest(trimmedSegment));
    }
  };

  return (
    <div className="flex flex-col gap-4 relative">
      <div className="flex flex-col gap-4 m-5 mb-20">
        <div className="flex items-center gap-3 p-5 bg-white border-2 border-border rounded-lg shadow-sm hover:shadow-md transition-shadow">
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={nameSaving}
                className="flex-1 text-xl py-2.5 px-4 font-semibold bg-background border-2 border-primary rounded-lg text-foreground focus:outline-none focus:ring-0 transition-all disabled:opacity-70"
                placeholder="Enter segment name"
              />
              <button
                onClick={handleSave}
                disabled={nameSaving}
                className="p-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                title="Save (Enter)"
              >
                {nameSaving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleCancel}
                disabled={nameSaving}
                className="p-2.5 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cancel (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-foreground flex-1">
                {segment.name}
              </h1>
              <button
                onClick={handleEdit}
                className="p-2 text-muted-foreground hover:text-foreground bg-muted/80 hover:bg-muted rounded-lg transition-colors"
                title="Edit segment name"
              >
                <Edit3 className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
        <div className="w-full gap-3 relative">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Filter className="w-5 h-5 text-primary" aria-hidden />
            <h2 className="text-lg font-semibold text-foreground">
              Segment conditions
            </h2>
          </div>
          <SegmentsCondition segment={segment} setSegment={setSegment} />
        </div>
      </div>
      <div className="fixed bottom-0 w-full bg-white border-t border-gray-200 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2">
            <button
              className="bg-primary flex items-center gap-2 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary hover:border-primary transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={segmentLoading}
              onClick={handleSubmit}
            >
              {segmentLoading && <Loader2 className="w-4 h-4 animate-spin" />}{' '}
              {segmentLoading ? 'Saving...' : 'Save segment'}
            </button>
            <button
              className="bg-white text-destructive border border-destructive px-4 py-2 rounded-lg font-medium hover:bg-destructive hover:text-white hover:border-destructive transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={segmentLoading}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartSegmentsForm;
